<template>
  <div v-if="floorPlans.length > 0" class="floor-plan-container" :class="{ expanded: isExpanded, 'always-show': alwaysShow }">
    <!-- Toggle Button -->
    <button class="floor-plan-toggle" @click="isExpanded = !isExpanded" :title="isExpanded ? 'Hide floor plan' : 'Show floor plan'">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="21" x2="9" y2="9"/>
      </svg>
      <span v-if="!isExpanded" class="toggle-label">Floor Plan</span>
    </button>

    <!-- Floor Plan Panel -->
    <div v-show="isExpanded" class="floor-plan-panel">
      <!-- Floor selector (if multiple) -->
      <div v-if="floorPlans.length > 1" class="floor-selector">
        <button
          v-for="(fp, idx) in floorPlans"
          :key="fp.uuid"
          class="floor-btn"
          :class="{ active: activeFloorIndex === idx }"
          @click="activeFloorIndex = idx"
        >
          {{ fp.name || `Floor ${idx + 1}` }}
        </button>
      </div>

      <!-- Floor plan image with hotspots -->
      <div class="floor-plan-image-container" ref="imageContainer">
        <img
          :src="activeFloorPlan?.image_url"
          :alt="activeFloorPlan?.name || 'Floor Plan'"
          class="floor-plan-image"
          @load="onImageLoaded"
        />
        <!-- Hotspot dots -->
        <button
          v-for="hotspot in activeHotspots"
          :key="hotspot.scene_uuid"
          class="floor-hotspot"
          :class="{ active: String(hotspot.scene_uuid) === String(activeSceneId) }"
          :style="{ left: hotspot.x + '%', top: hotspot.y + '%' }"
          :title="hotspot.label || hotspot.scene_uuid"
          @click="$emit('navigate', hotspot.scene_uuid)"
        >
          <span class="hotspot-dot"></span>
          <span v-if="hotspot.label" class="hotspot-label">{{ hotspot.label }}</span>
        </button>
        <!-- Current position indicator pulse -->
        <div
          v-if="currentPosition"
          class="current-position"
          :style="{ left: currentPosition.x + '%', top: currentPosition.y + '%' }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

interface FloorPlanHotspot {
  scene_uuid: string
  x: number // percentage 0-100
  y: number // percentage 0-100
  label?: string
}

interface FloorPlan {
  uuid: string
  name: string
  image_url: string
  hotspots: FloorPlanHotspot[]
  order_index: number
}

const props = defineProps<{
  floorPlans: FloorPlan[]
  activeSceneId: string | null
  alwaysShow?: boolean
}>()

defineEmits<{
  (e: 'navigate', sceneUuid: string): void
}>()

const isExpanded = ref(props.alwaysShow || false)
const activeFloorIndex = ref(0)
const imageContainer = ref<HTMLElement>()

const activeFloorPlan = computed(() => props.floorPlans[activeFloorIndex.value])
/** Safely parse hotspots — API should always return an array, but guard against JSON strings from cached R2 */
function safeHotspots(raw: any): FloorPlanHotspot[] {
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [] } catch { return [] }
  }
  return []
}

const activeHotspots = computed(() => safeHotspots(activeFloorPlan.value?.hotspots))

// Find current position on floor plan based on active scene
const currentPosition = computed(() => {
  if (!props.activeSceneId || !activeHotspots.value.length) return null
  const hotspot = activeHotspots.value.find(h => String(h.scene_uuid) === String(props.activeSceneId))
  return hotspot ? { x: hotspot.x, y: hotspot.y } : null
})

// Auto-switch to floor that contains the active scene
watch(() => props.activeSceneId, (sceneId) => {
  if (!sceneId) return
  for (let i = 0; i < props.floorPlans.length; i++) {
    if (safeHotspots(props.floorPlans[i].hotspots).some(h => String(h.scene_uuid) === String(sceneId))) {
      activeFloorIndex.value = i
      break
    }
  }
})

function onImageLoaded() {
  // Image loaded — hotspots will position correctly via CSS percentages
}
</script>

<style scoped>
.floor-plan-container {
  position: absolute;
  top: 110px;
  right: 16px;
  z-index: 20;
  font-family: system-ui, -apple-system, sans-serif;
}

.floor-plan-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: white;
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition: all 0.2s;
}

.floor-plan-toggle:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.3);
}

.toggle-label {
  display: none;
}

.floor-plan-panel {
  position: absolute;
  top: 0;
  right: 48px;
  width: 320px;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 400px;
}

.floor-selector {
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  overflow-x: auto;
}

.floor-btn {
  padding: 4px 10px;
  background: rgba(51, 65, 85, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 6px;
  color: #94a3b8;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  transition: all 0.15s;
}

.floor-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.floor-plan-image-container {
  position: relative;
  padding: 8px;
}

.floor-plan-image {
  width: 100%;
  height: auto;
  border-radius: 6px;
  display: block;
}

.floor-hotspot {
  position: absolute;
  transform: translate(-50%, -50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 2;
}

.hotspot-dot {
  display: block;
  width: 14px;
  height: 14px;
  background: #3b82f6;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  transition: all 0.2s;
}

.floor-hotspot:hover .hotspot-dot {
  transform: scale(1.3);
  background: #2563eb;
}

.floor-hotspot.active .hotspot-dot {
  background: #ef4444;
  transform: scale(1.3);
  animation: pulse-dot 2s infinite;
}

.hotspot-label {
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.9);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.floor-hotspot:hover .hotspot-label {
  opacity: 1;
}

.current-position {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: rgba(239, 68, 68, 0.3);
  border-radius: 50%;
  animation: pulse-position 2s infinite;
  z-index: 1;
  pointer-events: none;
}

@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
}

@keyframes pulse-position {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
  50% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
}

/* Always-show mini-map mode */
.floor-plan-container.always-show {
  top: auto;
  bottom: 80px;
  right: 16px;
}

.floor-plan-container.always-show .floor-plan-toggle {
  display: none;
}

.floor-plan-container.always-show .floor-plan-panel {
  position: relative;
  top: 0;
  right: 0;
  width: 200px;
  max-height: 200px;
  opacity: 0.85;
  transition: opacity 0.2s;
}

.floor-plan-container.always-show:hover .floor-plan-panel {
  opacity: 1;
  width: 300px;
  max-height: 350px;
}

@media (max-width: 768px) {
  .floor-plan-container {
    top: 100px;
    right: 12px;
  }

  .floor-plan-toggle {
    width: 34px;
    height: 34px;
  }

  .floor-plan-panel {
    max-width: 260px;
    max-height: 300px;
  }

  .floor-plan-container.always-show {
    bottom: 60px;
    right: 8px;
  }

  .floor-plan-container.always-show .floor-plan-panel {
    width: 140px;
    max-height: 140px;
  }

  .floor-plan-container.always-show:hover .floor-plan-panel {
    width: 240px;
    max-height: 280px;
  }
}
</style>
